import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { Formik, FormikErrors, FormikProps } from "formik";
import { useRecoilState } from "recoil";
import { useFlags } from "launchdarkly-react-client-sdk";
import { PortalStatusBanner, PortalStatusFlag } from "@bto/shared";
import { Button } from "../shared/Button/Button";
import { GetTypographyContent } from "../shared/Typography/Typography";
import {
  ButtonContainer,
  CenterContainter,
  DesktopBrandLockup,
  DesktopBrandPanel,
  DesktopColorBackground,
  DesktopContainer,
  DesktopFormCell,
  DesktopLoginGradientLayer,
  LoginAccountWarning,
  LoginError,
  LoginFieldsSection,
  LoginFormColumn,
  LoginFormFieldInput,
  LoginFormWrapper,
  LoginHeaderBlock,
  LoginHeroBand,
  LoginHeroLockupImg,
  LoginInput,
  LoginPassword,
  LoginPrivacyPolicy,
  NonDesktopContents,
  NonDesktopShell,
  PasswordLinkContainer,
  StatusBannerWrapper,
} from "./Login.styles";
import { FeatureFlags } from "../../utils/launchDarklyUtils";
import unityLockup from "../../assets/logos/unity-rebrand/unity-vert-cobranded.svg";
import unityLockupHorizontal from "../../assets/logos/unity-rebrand/unity-horiz-cobranded.svg";
import LoginRequest, {
  LoginInitialValues,
  LoginRequestType,
} from "./Services/LoginService";
import { RESET_PASSWORD_URL } from "../../utils/urlConstants";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { patientState } from "../../recoil/atoms";
import { UserAuthClientResponse } from "../../recoil/types";
import { validateEmail } from "../../utils/hooks/helpers";
import { SpinnerLoading } from "../shared/Spinner/SpinnerLoading";
import { networkErrorMessage } from "../../utils/helpers";
import {
  getCookieAccessToken,
  removeCookieAccessToken,
  setCookieAccessToken,
} from "../../utils/requestUtils";
import { consumePostAuthPath } from "../../utils/postAuthRedirect";

export const LoginForm: React.FC<{
  setPathname: () => void;
}> = ({ setPathname }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { from, existingEmail } =
    (location.state as { from?: string; existingEmail?: string }) || {};
  const { isDesktop, deviceType } = useDeviceHook();
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [patientInfo, setPatientInfo] = useRecoilState(patientState);
  const flags = useFlags();
  const portalStatus = flags[FeatureFlags.PortalStatus] as
    | PortalStatusFlag
    | undefined;

  useEffect(() => {
    const unlisten = history.listen(() => {
      setPathname();
    });
    return () => {
      unlisten();
    };
  }, []);

  useEffect(() => {
    if (patientInfo && getCookieAccessToken()) {
      history.push(consumePostAuthPath());
    }
  }, [patientInfo]);

  const onSubmit = async ({
    email,
    password,
  }: LoginRequestType): Promise<void> => {
    setErrorMessage("");
    setIsLoading(true);
    try {
      const response = await LoginRequest({
        email,
        password,
      });
      setIsLoading(false);
      if (response?.ok) {
        const userData: UserAuthClientResponse = await response.json();
        setCookieAccessToken(userData.auth0.access_token);
        setPatientInfo(userData);
      } else {
        removeCookieAccessToken();
        setPatientInfo(undefined);
        setErrorMessage(t("login.errors.invalidCredentials"));
      }
    } catch (e) {
      setIsLoading(false);
      setErrorMessage(networkErrorMessage);
    }
  };

  const headerContent =
    from === "createAccount" ? (
      <>
        {t("login.linkedTest.titleLine1")}
        <br />
        {t("login.linkedTest.titleLine2")}
      </>
    ) : (
      t("login.title")
    );

  const loginSection = (
    <CenterContainter data-testid="loginCenterContainer">
      <LoginFormColumn>
        <LoginHeaderBlock>
          {GetTypographyContent({
            content: t("login.welcome"),
            size: "xlarge",
            textColor: "neutral700",
          })}
          {GetTypographyContent({
            content: headerContent,
            size: "h3",
            isBold: true,
            textColor: "greyMedium",
          })}
          {GetTypographyContent({
            content: t("login.subtitle"),
            size: "large",
            textColor: "greyMedium",
          })}
        </LoginHeaderBlock>
        {portalStatus?.enabled && (
          <StatusBannerWrapper deviceType={deviceType}>
            <PortalStatusBanner
              status={portalStatus.status}
              title={portalStatus.title}
              message={portalStatus.message}
              statusPageUrl={portalStatus.statusPageUrl}
            />
          </StatusBannerWrapper>
        )}
        {errorMessage && (
          <LoginError>
            {GetTypographyContent({
              content: errorMessage,
              size: "medium",
              textColor: "redDark",
            })}
          </LoginError>
        )}
        <Formik
          initialValues={{
            ...LoginInitialValues,
            email: existingEmail || LoginInitialValues.email,
          }}
          validateOnChange={false}
          validate={(values) => {
            const errors: FormikErrors<LoginRequestType> = {};
            if (!values.email) {
              errors.email = t("login.errors.required");
            }
            if (!values.password) {
              errors.password = t("login.errors.required");
            }
            if (!validateEmail(values.email)) {
              errors.email = t("login.errors.invalidEmail");
            }
            return errors;
          }}
          onSubmit={(values, { setSubmitting }) => {
            onSubmit(values);
            setSubmitting(false);
          }}
        >
          {({
            values,
            errors,
            setFieldError,
            handleSubmit,
            setFieldValue,
          }: FormikProps<LoginRequestType>) => (
            <LoginFormWrapper
              onKeyDown={(e) => {
                if (e.key === "Enter" && !isLoading) {
                  handleSubmit();
                }
              }}
            >
              <LoginFieldsSection deviceType={deviceType}>
                <LoginFormFieldInput deviceType={deviceType}>
                  <LoginInput
                    type="text"
                    name="email"
                    value={values.email}
                    label={t("login.fields.email")}
                    required
                    size="small"
                    onChange={(e) => {
                      setErrorMessage("");
                      setFieldError("email", "");
                      setFieldValue("email", e.target.value);
                    }}
                    error={errors.email}
                  />
                </LoginFormFieldInput>
                <LoginFormFieldInput deviceType={deviceType}>
                  <LoginPassword
                    type="password"
                    name="password"
                    value={values.password}
                    label={t("login.fields.password")}
                    required
                    size="small"
                    onChange={(e) => {
                      setErrorMessage("");
                      setFieldError("password", "");
                      setFieldValue("password", e.target.value);
                    }}
                    error={Boolean(errors.password)}
                    helperText={errors.password}
                  />
                </LoginFormFieldInput>
                <PasswordLinkContainer deviceType={deviceType}>
                  {GetTypographyContent({
                    content: t("login.forgotPassword"),
                    size: "small",
                    textColor: "greenDark",
                    link: RESET_PASSWORD_URL,
                  })}
                </PasswordLinkContainer>
              </LoginFieldsSection>
              <LoginPrivacyPolicy>
                <p>
                  {t("login.legal.prefix")}
                  <a
                    href="https://www.billiontoone.com/privacy-policy"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("login.legal.privacyPolicy")}
                  </a>
                  {t("login.legal.separator")}
                  <a
                    href="https://www.billiontoone.com/privacy-practices"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("login.legal.noticeOfPrivacyPractices")}
                  </a>
                  {t("login.legal.finalSeparator")}
                  <a
                    href="../../client/terms_of_use"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("login.legal.termsOfUse")}
                  </a>
                  .
                </p>
              </LoginPrivacyPolicy>
              {isLoading ? (
                <>
                  <SpinnerLoading />
                </>
              ) : (
                <ButtonContainer>
                  <Button
                    type="submit"
                    label={t("login.button")}
                    id="LoginButton"
                    size="medium"
                    onClick={() => handleSubmit()}
                    disabled={isLoading}
                    textColor="white"
                  />
                </ButtonContainer>
              )}
            </LoginFormWrapper>
          )}
        </Formik>
      </LoginFormColumn>
      <LoginAccountWarning>
        <Trans
          i18nKey="login.accountWarning"
          components={{
            lead: <p />,
          }}
        />
      </LoginAccountWarning>
    </CenterContainter>
  );

  return (
    <>
      {!patientInfo?.auth0 && isDesktop && (
        <DesktopContainer>
          <DesktopColorBackground>
            <DesktopLoginGradientLayer aria-hidden />
            <DesktopBrandPanel>
              <DesktopBrandLockup
                src={unityLockup}
                alt="Unity by BillionToOne"
              />
            </DesktopBrandPanel>
          </DesktopColorBackground>
          <DesktopFormCell>{loginSection}</DesktopFormCell>
        </DesktopContainer>
      )}
      {!patientInfo?.auth0 && !isDesktop && (
        <NonDesktopShell>
          <LoginHeroBand $deviceType={deviceType}>
            <LoginHeroLockupImg
              $deviceType={deviceType}
              src={unityLockupHorizontal}
              alt="Unity by BillionToOne"
            />
          </LoginHeroBand>
          <NonDesktopContents $deviceType={deviceType}>
            {loginSection}
          </NonDesktopContents>
        </NonDesktopShell>
      )}
    </>
  );
};
